import { toast } from "../hooks/use-toast";
import { GET_COMPANIES } from "../graphql/queries/getCompanies";
import { client } from "../lib/apollo";
import { GET_BUSINESSES } from "../graphql/queries/getBusinesses";
import { GET_OPTIONS } from "../graphql/queries/getOptions";
import { GET_WALLET_BY_ID } from "../graphql/queries/getWalletById";
import { GET_WALLETS_FOR_USER } from "../graphql/queries/getWalletsForUser";
import { GET_MEMBERS } from "../graphql/queries/getMembers";
import { GET_NEAR_BY_DRIVERS } from "../graphql/queries/getNearbyDrivers";
import moment from "moment";
import { GET_WALLETS_FROM_MEMBER } from "../graphql/queries/getWalletsFromMember";
import { GET_CUSTOMER_CARDS } from "../graphql/queries/retriveCustomerCards";
import { CREATE_NEW_PAYMENT_METHOD_MUTATION } from "../graphql/mutations/createNewPaymentMethod";
import { GET_COMPANIES_IN_REGION } from "../graphql/queries/getCompaniesInRegion";
import { GET_COMPANY } from "../graphql/queries/getCompany";

export const fetchCompanies = async () => {
  try {
    const response = await client.query({
      query: GET_COMPANIES,
    });
    if (response?.data?.getCompanies) {
      return response.data.getCompanies.map((s) => ({
        ...s,
        title: s.name,
      }));
    } else {
      return [];
    }
  } catch (error) {
    console.log("error ", error);
    const userInfo = typeof window !== 'undefined' ? JSON.parse(localStorage.getItem("userInfo") || "{}") : {};
    if (userInfo.token) {
      toast({
        title: "Error",
        description: "Failed to fetch companies. Please try again later.",
        variant: "destructive",
      });
    }
    return [];
  }
};

export const fetchCompaniesByRegion = async (country, city, serviceId) => {
  try {
    const response = await client.query({
      query: GET_COMPANIES_IN_REGION,
      variables: {
        country: country,
        serviceId: serviceId
      },
      skip: !country,
    });

    if (response?.data?.getCompaniesInRegion) {
      return response.data.getCompaniesInRegion;
    } else {
      return [];
    }
  } catch (error) {
    console.log("error ", error);
    const userInfo = typeof window !== 'undefined' ? JSON.parse(localStorage.getItem("userInfo") || "{}") : {};
    if (userInfo.token) {
      toast({
        title: "Error",
        description: "Failed to fetch companies. Please try again later.",
        variant: "destructive",
      });
    }
    return [];
  }
};

export const fetchCompany = async (companyId, token) => {
  try {
    const headers = token ? { authorization: `Bearer ${token}` } : {};

    const response = await client.query({
      query: GET_COMPANY,
      variables: { companyId },
      context: Object.keys(headers).length ? { headers } : undefined,
    });

    if (response?.data?.getCompany) {
      return response.data.getCompany
    } else {
      return null
    }
  } catch (error) {
    console.log("error ", error);
    toast({
      title: "Error",
      description: "Failed to fetch company. Please try again later.",
      variant: "destructive",
    });
  }
};

export const fetchBusinessAccounts = async (businessId, companyId) => {
  try {
    const response = await client.query({
      query: GET_BUSINESSES,
      variables: {
        businessId: businessId ? businessId : null,
        companyId: companyId ? String(companyId) : null,
      },
      fetchPolicy: "network-only",
    });

    if (response?.data?.getBusinesses) {
      return response.data.getBusinesses;
    } else {
      return [];
    }
  } catch (error) {
    console.log("error ", error);
    const userInfo = typeof window !== 'undefined' ? JSON.parse(localStorage.getItem("userInfo") || "{}") : {};
    if (userInfo.token) {
      toast({
        title: "Error",
        description: "Failed to fetch business accounts. Please try again later.",
        variant: "destructive",
      });
    }
    return [];
  }
};

export const fetchOtherOrderOptions = async () => {
  try {
    const response = await client.query({
      query: GET_OPTIONS,
    });

    if (response?.data?.getOptions) {
      return response.data.getOptions;
    } else {
      return [];
    }
  } catch (error) {
    console.log("error ", error);
    toast({
      title: "Error",
      description: "Failed to fetch other order options. Please try again later.",
      variant: "destructive",
    });
  }
};

export const fetchStripeListSources = async ({ userId, walletId }) => {
  try {
    const response = await client.query({
      query: GET_WALLET_BY_ID,
      variables: {
        userId: userId ? userId : null,
        userType: "client",
        walletId: walletId ? walletId : null,
      },
      fetchPolicy: "network-only",
    });

    if (response?.data?.getWalletById && response.data.getWalletById.stripe_id) {
      return response.data.getWalletById;
    } else {
      return [];
    }
  } catch (error) {
    console.log("error ", error);
    toast({
      title: "Error",
      description: "Failed to fetch wallets. Please try again later.",
      variant: "destructive",
    });
  }
};

export const fetchWalletsForUser = async (userId) => {
  try {
    const response = await client.query({
      query: GET_WALLETS_FOR_USER,
      variables: {
        userId: userId ? userId : null,
        userType: "client",
      },
    });

    if (response?.data?.getWalletsForUser) {
      return response.data.getWalletsForUser.filter(wallet => wallet.stripe_id);
    } else {
      return [];
    }
  } catch (error) {
    console.log("error ", error);
    toast({
      title: "Error",
      description: "Failed to fetch wallets. Please try again later.",
      variant: "destructive",
    });
  }
};

export const fetchMembers = async (userId) => {
  try {
    const response = await client.query({
      query: GET_MEMBERS,
      variables: {
        userId: userId ? userId : null,
        type: "business",
      },
    });

    if (response?.data?.getMembers) {
      return response.data.getMembers;
    } else {
      return [];
    }
  } catch (error) {
    console.log("error ", error);
    toast({
      title: "Error",
      description: "Failed to fetch members. Please try again later.",
      variant: "destructive",
    });
  }
};

export const fetchAllDrivers = async ({
  carType,
  scheduleDate,
  price,
  companyId,
  lat,
  lng,
  tripId,
}) => {
  try {
    const response = await client.query({
      query: GET_NEAR_BY_DRIVERS,
      variables: {
        latitude: lat,
        longitude: lng,
        serviceType: carType,
        provider: "vert",
        scheduleDate: !scheduleDate
          ? moment().format("YYYY-MM-DD HH:mm:ss")
          : scheduleDate
            ? moment(scheduleDate).format("YYYY-MM-DD HH:mm:ss")
            : null,
        showAll: true,
        companyId: companyId ? companyId : null,
        estimatedPrice: price ? Number((price * 100).toFixed(0)) : null,
        tripId: tripId ? tripId : null,
      },
      fetchPolicy: "network-only",
    });

    if (response?.data?.getNearbyDrivers) {
      return response.data.getNearbyDrivers;
    } else {
      console.log("error ", response.data.error);
      return [];
    }
  } catch (error) {
    console.log("error ", error);
    throw error;
  }
};

export const fetchCustomerPaymentCards = async (stripeId) => {
  try {
    const response = await client.query({
      query: GET_CUSTOMER_CARDS,
      variables: {
        stripeId,
      },
      skip: !stripeId,
      fetchPolicy: "network-only",
    });

    if (response?.data?.retriveCustomerCards) {
      return response.data.retriveCustomerCards;
    } else {
      console.log("error ", response.data.error);
      return [];
    }
  } catch (error) {
    console.log("error ", error);
    throw error;
  }
};

export const fetchWalletsFromMember = async ({ memberId, clientId }) => {
  try {
    const response = await client.query({
      query: GET_WALLETS_FROM_MEMBER,
      variables: {
        memberId: memberId ? memberId : null,
        clientId: clientId ? clientId : null,
      },
      fetchPolicy: "network-only",
    });

    if (response?.data?.getWalletsFromMember) {
      return response.data.getWalletsFromMember.filter(wallet => wallet.stripe_id);
    } else {
      console.log("error ", response.data.error);
      return [];
    }
  } catch (error) {
    console.log("error ", error);
    throw error;
  }
};

export const createNewPaymentMethod = async ({
  type,
  cardNumber,
  expiryMonth,
  expiryYear,
  cvc,
  clientId,
}) => {
  try {
    const response = await client.mutate({
      mutation: CREATE_NEW_PAYMENT_METHOD_MUTATION,
      variables: {
        type,
        cardNumber,
        expiryMonth,
        expiryYear,
        cvc,
        clientId,
      },
    });
    if (response?.data?.createNewPaymentMethod) {
      return response.data.createNewPaymentMethod;
    }
  } catch (error) {
    console.log("error ", error);
    throw error;
  }
};
